// Claude: extracted from Buyer.tsx — column definitions 20-Apr-2026
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Checkbox, Popover } from "antd";
import React from "react";
import moment from "moment-timezone";

export const FIXED_BUYER_COLS = new Set(["buyerCompanyName", "actions"]);

interface BuyerColDeps {
  buyerPlansData: any[];
  industryNodes: any[];
  visibleColKeys: string[];
  toggleCol: (key: string) => void;
  colPopoverOpen: boolean;
  setColPopoverOpen: (v: boolean) => void;
  onOverrideClick: (object: any) => void;
  onEditClick: (object: any) => void;
  onRewardsClick: (object: any) => void;
  onRenewClick: (object: any) => void;
}

export const getBuyerTableConfig = ({
  buyerPlansData, industryNodes, visibleColKeys, toggleCol, colPopoverOpen, setColPopoverOpen,
  onOverrideClick, onEditClick, onRewardsClick, onRenewClick,
}: BuyerColDeps) => {
  const allBuyerColumns: any[] = [
    { title: "Company Name", dataIndex: "buyerCompanyName", key: "buyerCompanyName", sorter: (a: any, b: any) => a.buyerCompanyName.localeCompare(b.buyerCompanyName) },
    { title: "Country", dataIndex: "buyerPersonCountry", key: "buyerPersonCountry" },
    { title: "City", dataIndex: "buyerPersonCity", key: "buyerPersonCity" },
    { title: "State", dataIndex: "buyerPersonState", key: "buyerPersonState" },
    { title: "Timezone", dataIndex: "buyerTimezone", key: "buyerTimezone", render: (v: string) => v || <Text color="gray.400" fontSize="xs">—</Text> },
    { title: "Website", dataIndex: "buyerCompanyWebsite", key: "buyerCompanyWebsite", render: (v: string) => v || <Text color="gray.400" fontSize="xs">—</Text> },
    {
      title: "Company Info", dataIndex: "buyerCompanyInfo", key: "buyerCompanyInfo",
      render: (v: string) => v
        ? <span title={v} style={{ display: "block", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
        : <Text color="gray.400" fontSize="xs">—</Text>,
    },
    {
      title: "Industry", dataIndex: "industryIds", key: "industryIds",
      render: (ids: any[]) => {
        if (!ids?.length) return <Text color="gray.400" fontSize="xs">—</Text>;
        const names = ids.map((id: any) => industryNodes.find((n: any) => String(n._id) === String(id))?.name).filter(Boolean);
        return names.length ? <span>{names.join(", ")}</span> : <Text color="gray.400" fontSize="xs">—</Text>;
      },
    },
    { title: "Person Name", dataIndex: "buyerPersonName", key: "buyerPersonName", sorter: (a: any, b: any) => a.buyerPersonName.localeCompare(b.buyerPersonName) },
    { title: "Official Email", dataIndex: "buyerOfficialEmail", key: "buyerOfficialEmail" },
    { title: "Social Email", dataIndex: "buyersocialEmail", key: "buyersocialEmail", render: (v: string) => v || <Text color="gray.400" fontSize="xs">—</Text> },
    { title: "Phone", dataIndex: "buyerPhoneNumber", key: "buyerPhoneNumber" },
    { title: "Designation", dataIndex: "buyerDesignation", key: "buyerDesignation", render: (v: string) => v || <Text color="gray.400" fontSize="xs">—</Text> },
    { title: "LinkedIn Verified", dataIndex: "linkedinVerified", key: "linkedinVerified", render: (v: any) => v ? <span style={{ color: "#2E7D32" }}>Yes</span> : <span style={{ color: "#999" }}>No</span> },
    {
      title: "Plan", dataIndex: "subscriptionPlanId", key: "subscriptionPlanId",
      render: (_: any, object: any) => {
        const plan = buyerPlansData.find((p: any) => p._id === String(object.subscriptionPlanId));
        return plan
          ? <Text fontSize="xs" color={object.isPlanActive ? "green.600" : "orange.500"} fontWeight={500}>{plan.planName}</Text>
          : <Text fontSize="xs" color="gray.400">—</Text>;
      },
    },
    { title: "Plan Active", dataIndex: "isPlanActive", key: "isPlanActive", render: (v: any) => v ? <span style={{ color: "#2E7D32" }}>Yes</span> : <span style={{ color: "#856404" }}>No</span> },
    {
      title: "Company Type", dataIndex: "buyerCompanyType", key: "buyerCompanyType",
      render: (_: any, object: any) => {
        const types: string[] = object.buyerCompanyType || [];
        if (!types.length) return <Text color="gray.400" fontSize="xs">—</Text>;
        const display = types.includes("Others") && object.buyerCompanyTypeOther
          ? [...types.filter((t: string) => t !== "Others"), object.buyerCompanyTypeOther]
          : types;
        return <span>{display.join(", ")}</span>;
      },
    },
    {
      title: "Verification", dataIndex: "verificationStatus", key: "verificationStatus",
      render: (_: any, object: any) => {
        const status = object.verificationStatus || "unverified";
        const colorMap: Record<string, string> = {
          verified: "#2E7D32", unverified: "#856404", provisional: "#4F46E5",
          blocked: "#C62828", resubmitted: "#1565C0", pendingNewUser: "#E65100",
        };
        return (
          <Flex gap={1} alignItems="center" flexWrap="wrap">
            <span style={{ color: colorMap[status] || "#999" }}>{status}</span>
            <Button size="xs" h="20px" px={2} minW="fit-content" background="#0E122F" color="white" fontSize="11px" _hover={{ background: "#2E7D32" }}
              onClick={() => onOverrideClick(object)}
            >
              Override
            </Button>
          </Flex>
        );
      },
    },
    {
      title: "Actions", dataIndex: "actions", key: "actions",
      render: (_: any, object: any) => (
        <Flex gap={1}>
          <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={2} _hover={{ background: "#2E7D32" }} onClick={() => onEditClick(object)}>Edit</Button>
          <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={2} _hover={{ background: "#2E7D32" }} onClick={() => onRewardsClick(object)}>Rewards</Button>
          {object.subscriptionPlanId && !object.isPlanActive && (
            <Button size="xs" fontSize="xs" background="orange.500" color="white" px={2} _hover={{ background: "orange.600" }} onClick={() => onRenewClick(object)}>Renew</Button>
          )}
        </Flex>
      ),
    },
  ];

  const columns = [
    ...allBuyerColumns.filter(col => col.key !== "actions" && (FIXED_BUYER_COLS.has(col.key) || visibleColKeys.includes(col.key))),
    allBuyerColumns.find(col => col.key === "actions")!,
  ];

  const colChooserContent = (
    <Box p={1} style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 170 }}>
      {allBuyerColumns
        .filter(col => !FIXED_BUYER_COLS.has(col.key))
        .map(col => (
          <Checkbox key={col.key} checked={visibleColKeys.includes(col.key)} onChange={() => toggleCol(col.key)} style={{ fontSize: 12 }}>
            {col.title}
          </Checkbox>
        ))
      }
    </Box>
  );

  return { columns, colChooserContent };
};
